/**
 * Página: Punteo de un grupo de pedidos de Nave 2
 * Muestra los productos agrupados de varios pedidos y reparte el punteo con FIFO
 */
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { Box, Card, CircularProgress, Divider, Typography } from "@mui/material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { useNavigate, useParams } from "react-router-dom";
import { ListOrder, InfoGrupo } from "../components";
import SalesService from "../../../services/sales.service";
import Order from "../../../models/Order";
import ProductFactory from "../../../models/ProductFactory";
import { StateContext } from "../../../components/context/StateProvider";
import OrderService from "../../../services/order.service";
import Utils from "../../../utils/Utils";
import GruposService from "../api/grupos.api";
import Toasts from "../../../components/notificacion/Toasts";
import { getErrorMessage } from "../../../utils/ErrorUtils";
import { ORDER_STATE_N2 } from "../../../config/constants";
import { obtenerPreparadoresNave1 } from "../helpers/preparadorNave1";

// Estados que devuelve el backend para un grupo, traducidos a los estados de orden
// que entiende InfoOrder. COMPLETADO es PREPARADO ('3'), no EN_PREPARACION ('2').
const ESTADO_GRUPO_A_ORDER_STATE = {
    COMPLETADO: ORDER_STATE_N2.PREPARADO,
    EN_PROCESO: ORDER_STATE_N2.EN_PREPARACION,
    PENDIENTE: ORDER_STATE_N2.SIN_PREPARAR
};

/**
 * Estado de grupo que corresponde a un cambio de estado de los pedidos.
 * El grupo solo llega a EN_PROCESO por acciones del usuario (imprimir etiqueta, pasar a
 * revisión, marcar preparado): el COMPLETADO lo decide el backend al recalcular el punteo,
 * así que aquí nunca se fuerza.
 */
const estadoGrupoParaOrderState = (orderState) =>
    orderState === ORDER_STATE_N2.SIN_PREPARAR ? 'PENDIENTE' : 'EN_PROCESO';

const OrderN2Grupo = () => {

    const { grupoId } = useParams();
    const navigate = useNavigate();
    const { state, updateState } = useContext(StateContext);

    // Los callbacks que recibe ListOrder (registerAction, onAssignToOrder) nacen en un
    // render y se invocan mucho después, así que su closure congelaría el estado de
    // entonces. Esta ref replica lo que hacía this.context en la clase: leer siempre el
    // valor actual. Es imprescindible para registerSaleGrupo, que busca el producto en
    // state.products cargado tras el montaje, y para showError, que acumula sobre los
    // toasts que ya hubiera.
    const estadoRef = useRef(state);
    estadoRef.current = state;

    const preparadoresN1Ref = useRef(null);

    /**
     * Muestra un toast de error o advertencia
     */
    const showError = useCallback((mensaje, severity = 'error') => {
        const toasts = Toasts.generateToast(mensaje, severity);
        updateState({ toasts: [...estadoRef.current.toasts, toasts] });
    }, [updateState]);

    /**
     * Construye order, productos agrupados y pedidos únicos a partir del grupo
     * DECISION: Carga productos directamente de los pedidos de N2, no del grupo
     * Esto asegura que siempre tengamos EAN completo, stock y datos actualizados
     */
    const buildGrupoState = useCallback(async (grupo) => {
        const year = estadoRef.current.year;

        let order = new Order();
        order.num = grupo.id;  // ID del grupo
        order.n2 = true;
        order.tipo = 'grupo';
        order.nombre = grupo.nombre;
        order.route = 'Grupo';
        order.preparer = grupo.preparador || '0';
        order.state = ESTADO_GRUPO_A_ORDER_STATE[grupo.estado] || ORDER_STATE_N2.SIN_PREPARAR;
        order.camera = null; // Sin cámara en grupos

        // 1. Cargar productos de cada pedido del grupo en paralelo
        const productosPorPedido = await Promise.all(
            grupo.pedidos.map(async (pedidoInfo) => {
                try {
                    const numero = pedidoInfo.numero || pedidoInfo;
                    const tipo = pedidoInfo.tipo || 'albaran';

                    const productos = await SalesService.getSalesProductN2(
                        numero,
                        year,
                        tipo
                    );

                    return { pedido: pedidoInfo, productos };
                } catch (error) {
                    console.error(`Error al cargar productos del pedido ${pedidoInfo.numero || pedidoInfo}:`, error);
                    showError(`Error al cargar pedido ${pedidoInfo.numero || pedidoInfo}`, 'warning');
                    return { pedido: pedidoInfo, productos: [] };
                }
            })
        );

        // 2. Agrupar productos por referencia
        const productosMap = new Map(); // ref → producto agrupado

        // DECISION: Determinar nombre de propiedad EAN según año (igual que OrderN2)
        let barrasModels = 'BarrasModels';
        if (year !== Utils.getCurrentYear()) {
            barrasModels = 'BarrasModel' + year + 's';
        }

        for (const { pedido, productos } of productosPorPedido) {
            for (const prod of productos) {
                const ref = prod.ARTICULO || prod.REF || prod.REFERENCIA;

                if (!ref) {
                    console.warn('Producto sin referencia, saltando:', prod);
                    continue;
                }

                if (!productosMap.has(ref)) {
                    // Primer producto con esta referencia
                    // DECISION: Los datos del producto están en INFO_ARTICULO (igual que OrderN2)
                    const infoArticulo = prod.INFO_ARTICULO || {};

                    productosMap.set(ref, {
                        ref: ref,
                        ean: infoArticulo[barrasModels] || [], // Array completo de EAN
                        nombre: infoArticulo.NOMBRE || '',
                        ubicacion: infoArticulo.UBICACION || '',
                        stock: infoArticulo.Stocks2Models || [], // Stock por almacenes
                        venserie: infoArticulo.VENSERIE || false,
                        totalUnidades: 0,
                        totalRegistrado: 0,
                        pedidos: []
                    });
                }

                const productoAgrupado = productosMap.get(ref);

                // Obtener registro del producto en este pedido
                let registrado = 0;
                if (!Utils.checkRefProduct(ref)) {
                    const numero = pedido.numero || pedido;
                    const tipo = pedido.tipo || 'albaran';

                    const saleRegistry = await SalesService.getSalesProduct(
                        numero,
                        ref,
                        year,
                        2, // nave 2
                        tipo
                    );
                    registrado = saleRegistry.length > 0 ? saleRegistry[0].unidades : 0;
                }

                const unidades = prod.UNIDADES || prod.CANTIDAD || 0;

                productoAgrupado.totalUnidades += unidades;
                productoAgrupado.totalRegistrado += registrado;

                productoAgrupado.pedidos.push({
                    numero: pedido.numero,
                    tipo: pedido.tipo,
                    fecha: pedido.fecha,
                    cliente: pedido.cliente,
                    ruta: pedido.ruta,
                    unidades: unidades,
                    registrado: registrado,
                    pendiente: unidades - registrado
                });
            }
        }

        // 3. Convertir a array y crear productos para el DataGrid
        let complete = 0;
        let grupoTotalUnits = 0;
        let grupoTotalRegister = 0;

        const products = Array.from(productosMap.values()).map((productoAgrupado, index) => {
            const totalRemaining = productoAgrupado.totalUnidades - productoAgrupado.totalRegistrado;
            const totalState = totalRemaining === 0 ? 1 : 0;

            grupoTotalUnits += productoAgrupado.totalUnidades;
            grupoTotalRegister += productoAgrupado.totalRegistrado;

            if (totalRemaining === 0) {
                complete += 1;
            }

            // Crear producto con TODOS los datos (EAN completo, stock)
            let product = ProductFactory.productOrder(
                grupo.id,
                productoAgrupado.ref,
                productoAgrupado.ean, // Array completo de EAN
                productoAgrupado.nombre,
                productoAgrupado.totalUnidades,
                productoAgrupado.ubicacion,
                productoAgrupado.venserie, // venserie del INFO_ARTICULO
                totalRemaining,
                totalState,
                productoAgrupado.totalRegistrado,
                false, // duplicate (grupos no tienen duplicados)
                null, // line
                undefined, // registerDate
                false, // manual
                index
            );

            // Añadir stock y pedidos para distribución FIFO
            product.stock = productoAgrupado.stock;
            product.pedidos = productoAgrupado.pedidos;
            product.pedidosCount = productoAgrupado.pedidos.length;

            return product;
        });

        // Preparadores de nave 1 para el ticket impreso. Los pedidos de un grupo no cambian,
        // así que se consultan una vez y se reutilizan en las recargas tras cada punteo
        if (!preparadoresN1Ref.current) {
            preparadoresN1Ref.current = await obtenerPreparadoresNave1(
                grupo.pedidos.map((p) => ({ numero: p.numero || p, tipo: p.tipo || 'albaran' })),
                year
            );
        }
        order.preparadoresN1 = preparadoresN1Ref.current;

        order.complete = complete === products.length;
        order.progress = grupoTotalUnits > 0
            ? Math.round((grupoTotalRegister / grupoTotalUnits) * 100)
            : 0;
        order.totalUnits = grupoTotalUnits;
        order.totalRegister = grupoTotalRegister;

        // Extraer pedidos únicos para mostrar en la cabecera
        const pedidos = grupo.pedidos.map(p => ({
            numero: p.numero,
            tipo: p.tipo,
            fecha: p.fecha,
            cliente: p.cliente,
            ruta: p.ruta
        }));

        return { order, products, pedidos };
    }, [showError]);

    // Carga inicial y limpieza del estado global al salir. Deps vacías a propósito: es el
    // equivalente a componentDidMount/componentWillUnmount, que corrían una sola vez. El
    // ejercicio no hace falta en deps porque Content.js monta las páginas con
    // key={state.year}: cambiarlo remonta el componente en vez de re-renderizarlo.
    useEffect(() => {
        const cargarGrupo = async () => {
            updateState({ loading: true });

            try {
                const grupo = await GruposService.getById(grupoId, estadoRef.current.year);

                if (!grupo) {
                    console.error('[OrderN2Grupo] Grupo no encontrado');
                    showError('Grupo no encontrado');
                    navigate('/pedidosn2/grupos');
                    return;
                }

                // DECISION: Validar pedidos en lugar de productos
                // El backend ya NO devuelve productos agrupados, solo pedidos
                if (!grupo.pedidos || grupo.pedidos.length === 0) {
                    console.error('[OrderN2Grupo] Grupo sin pedidos:', grupo);
                    showError('El grupo no tiene pedidos asociados', 'warning');
                    navigate('/pedidosn2/grupos');
                    return;
                }

                const { order, products, pedidos } = await buildGrupoState(grupo);

                // Validar que se hayan cargado productos
                if (!products || products.length === 0) {
                    console.error('[OrderN2Grupo] No se cargaron productos');
                    showError('No se pudieron cargar productos de los pedidos del grupo', 'warning');
                    navigate('/pedidosn2/grupos');
                    return;
                }

                updateState({
                    order: order,
                    products: products,
                    pedidosGrupo: pedidos,
                    filters: [],
                    loading: false
                });

            } catch (error) {
                console.error('💥 [OrderN2Grupo] Error al cargar grupo:', error);
                showError(getErrorMessage(error, 'Error al cargar el grupo'));
                updateState({ loading: false });
                navigate('/pedidosn2/grupos');
            }
        };

        cargarGrupo();

        return () => {
            updateState({ toasts: [] });
            updateState({ products: [] });
            updateState({ pedidosGrupo: [] });
            updateState({ order: new Order() });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Recargar productos después de puntear
     */
    const reloadProducts = useCallback(async () => {
        try {
            const grupo = await GruposService.getById(grupoId, estadoRef.current.year);
            const { order, products, pedidos } = await buildGrupoState(grupo);

            updateState({
                order: order,
                products: products,
                pedidosGrupo: pedidos
            });

        } catch (error) {
            console.error('Error al recargar productos del grupo:', error);
            showError(getErrorMessage(error, 'Error al recargar productos'));
        }
    }, [buildGrupoState, showError, updateState, grupoId]);

    /**
     * Verificar si un pedido está completo y, si lo está, marcarlo como preparado
     * @returns {Promise<boolean>} true si el pedido se marcó como preparado
     */
    const checkAndUpdateOrderState = useCallback(async (numeroPedido, tipoPedido, year) => {
        try {
            // Obtener todos los productos del pedido
            const productosPedido = await SalesService.getSalesProductN2(
                numeroPedido,
                year,
                tipoPedido
            );

            if (!productosPedido || productosPedido.length === 0) {
                return false;
            }

            // DECISION: No confiar en el campo PENDIENTES porque podría estar en caché
            // Calcular manualmente si el pedido está completo
            let todoCompleto = true;

            for (const producto of productosPedido) {
                const unidadesEsperadas = producto.UNIDADES || 0;
                const ref = producto.ARTICULO || producto.REF || producto.REFERENCIA;

                // Obtener registro ACTUALIZADO directamente
                const saleRegistry = await SalesService.getSalesProduct(
                    numeroPedido,
                    ref,
                    year,
                    2, // nave 2
                    tipoPedido
                );

                const unidadesRegistradas = saleRegistry.length > 0 ? saleRegistry[0].unidades : 0;
                const pendiente = unidadesEsperadas - unidadesRegistradas;

                if (pendiente > 0) {
                    todoCompleto = false;
                    break;
                }
            }

            if (!todoCompleto) {
                return false;
            }

            // Cambiar estado a "3" (preparado en N2)
            await OrderService.assignToOrder(
                numeroPedido,
                null, // reviewer
                null, // preparer
                'ventas', // handle
                ORDER_STATE_N2.PREPARADO, // estado preparado N2
                true, // n2
                undefined, // exclude
                year,
                null, // camera
                tipoPedido,
                null, // location
                null // imagenes
            );

            return true;

        } catch (error) {
            console.error(`Error al cambiar estado de ${numeroPedido}:`, error);
            showError(`No se pudo marcar como preparado el pedido ${numeroPedido}`, 'warning');
            return false;
        }
    }, [showError]);

    /**
     * Función de registro que distribuye entre pedidos del grupo
     */
    const registerSaleGrupo = useCallback(async (orderNum, refArticulo, unitsRegister, registerDate, manual, year, nave, type) => {
        try {
            // DECISION: Buscar producto en state.products (construido en buildGrupoState)
            // ya que el backend ahora NO devuelve productos agrupados
            const currentProduct = estadoRef.current.products.find(p => p.ref === refArticulo);

            if (!currentProduct) {
                throw new Error('Producto no encontrado en el grupo');
            }

            if (!currentProduct.pedidos || currentProduct.pedidos.length === 0) {
                throw new Error('El producto no tiene pedidos asociados');
            }

            // Obtener unidades ya registradas
            const unidadesNuevas = unitsRegister - (currentProduct.register || 0);

            // DECISION: en un grupo no se puede restar unidades porque no se sabe
            // de qué pedido descontarlas; solo se admiten incrementos
            if (unidadesNuevas <= 0) {
                showError('No se pueden restar unidades en un grupo, solo añadir', 'warning');
                return { message: 'No hay unidades nuevas para registrar' };
            }

            // Distribuir unidades entre pedidos usando FIFO (más antiguo primero)
            let unidadesRestantes = unidadesNuevas;
            const pedidosOrdenados = [...currentProduct.pedidos].sort((a, b) =>
                new Date(a.fecha) - new Date(b.fecha)
            );

            for (const pedido of pedidosOrdenados) {
                if (unidadesRestantes <= 0) break;

                // Obtener cuánto queda pendiente en este pedido
                const saleRegistry = await SalesService.getSalesProduct(
                    pedido.numero,
                    refArticulo,
                    year,
                    nave,
                    pedido.tipo
                );

                const registradoActual = saleRegistry.length > 0 ? saleRegistry[0].unidades : 0;
                const pendiente = pedido.unidades - registradoActual;

                if (pendiente > 0) {
                    const unidadesARegistrar = Math.min(pendiente, unidadesRestantes);

                    await SalesService.registerSale(
                        pedido.numero,
                        refArticulo,
                        registradoActual + unidadesARegistrar,
                        registerDate,
                        manual,
                        year,
                        nave,
                        pedido.tipo
                    );

                    unidadesRestantes -= unidadesARegistrar;
                }
            }

            if (unidadesRestantes > 0) {
                showError(`Quedaron ${unidadesRestantes} unidades sin repartir: los pedidos del grupo ya están completos`, 'warning');
            }

            // DECISION: Verificar TODOS los pedidos que tienen este producto
            // Esto asegura que si un pedido ya estaba completo, también se marque como preparado
            const pedidosUnicos = new Map();
            currentProduct.pedidos.forEach(p => {
                pedidosUnicos.set(p.numero, p.tipo);
            });

            let algunPedidoCompletado = false;
            for (const [numeroPedido, tipoPedido] of pedidosUnicos) {
                const completado = await checkAndUpdateOrderState(numeroPedido, tipoPedido, year);
                algunPedidoCompletado = algunPedidoCompletado || completado;
            }

            // Sincronizar estado del grupo una sola vez si cambió algún pedido
            if (algunPedidoCompletado) {
                await GruposService.updateEstado(grupoId, year);
            }

            // Recargar productos del grupo
            await reloadProducts();

            return { message: 'Producto registrado correctamente' };

        } catch (error) {
            console.error('Error al registrar producto en grupo:', error);
            showError(getErrorMessage(error, 'Error al registrar producto'));
            throw error; // Re-throw para que ListOrder lo maneje
        }
    }, [showError, checkAndUpdateOrderState, reloadProducts, grupoId]);

    /**
     * Interceptar asignación para distribuir a todos los pedidos del grupo
     * @param {string} orderNum - Número del grupo
     * @param {string} reviewer - Revisor
     * @param {string} preparer - Preparador
     * @param {string} order - Handle (ventas/compras)
     * @param {string} orderStateN2 - Estado N2
     * @param {boolean} n2 - Es N2
     * @param {*} exclude - Excluir
     * @param {number} year - Año
     * @param {string} camera - Cámara
     * @param {string} type - Tipo de pedido
     * @param {string} location - Ubicación
     * @param {*} imagenes - Imágenes
     * @param {string} fieldType - Tipo de campo que se está asignando
     * @returns {Promise}
     */
    const handleAssignToOrder = useCallback(async (orderNum, reviewer, preparer, order, orderStateN2, n2, exclude, year, camera, type, location, imagenes, fieldType) => {
        try {
            // DECISION: Usar pedidosGrupo del estado en lugar de recargar
            // Esto es más eficiente y evita inconsistencias
            const pedidosGrupo = estadoRef.current.pedidosGrupo || [];

            if (pedidosGrupo.length === 0) {
                throw new Error('No hay pedidos en el grupo');
            }

            // Obtener pedidos únicos del grupo
            const pedidosUnicos = new Map();
            pedidosGrupo.forEach(pedido => {
                pedidosUnicos.set(pedido.numero, pedido.tipo);
            });

            // Asignar a cada pedido del grupo
            await Promise.all(
                Array.from(pedidosUnicos.entries()).map(([numero, tipoPedido]) =>
                    OrderService.assignToOrder(
                        numero,
                        reviewer,
                        preparer,
                        order,
                        orderStateN2,
                        n2,
                        exclude,
                        estadoRef.current.year,
                        camera,
                        tipoPedido,
                        location,
                        imagenes
                    )
                )
            );

            // Guardar el preparador en el grupo. Es solo metadata: el endpoint /preparador
            // NO toca el estado (ver api/docs/api/grupos-n2.md). El grupo pasa a EN_PROCESO
            // únicamente cuando /estado detecta punteo registrado.
            if (preparer && fieldType === 'preparer') {
                await GruposService.updatePreparador(grupoId, preparer);

                const toasts = Toasts.generateToast('Preparador asignado correctamente', 'success');
                updateState({ toasts: [...estadoRef.current.toasts, toasts] });
            }

            // Sincronizar el estado del grupo cuando la acción cambia el de los pedidos:
            // imprimir etiqueta, pasar a revisión o marcar preparado a mano. Se detecta por
            // orderStateN2 en vez de por fieldType porque InfoOrder lo rellena en todas esas
            // ramas, incluida la del revisor, y FIELD_TYPE no se exporta.
            if (orderStateN2) {
                // Sin recargar: InfoOrder actualiza order.state en su .then con el objeto que
                // capturó antes de la promesa, así que una recarga aquí se perdería.
                await GruposService.updateEstado(
                    grupoId,
                    estadoRef.current.year,
                    estadoGrupoParaOrderState(orderStateN2)
                );
            }

            return { message: 'Asignado correctamente' };

        } catch (error) {
            console.error('Error al asignar:', error);
            showError(getErrorMessage(error, 'Error al asignar'));
            throw error; // Re-throw para que InfoOrder lo maneje
        }
    }, [showError, updateState, grupoId]);

    // Leer datos del estado (cargados en el efecto de montaje)
    const { order, loading } = state;
    const pedidosArray = state.pedidosGrupo || [];

    // Grupo ya en marcha: al entrar se baja al campo de escaneo y se enfoca, para
    // poder seguir punteando sin tocar la pantalla
    const punteoEnMarcha =
        order?.state === ORDER_STATE_N2.EN_PREPARACION &&
        Boolean(order?.preparer) &&
        order.preparer !== '0';

    // DECISION: misma estructura que SalesN2Grupos: una única Card sin padding propio
    // y una sección Box por bloque. El responsive va con sx { xs, md }, heredado de
    // cuando era componente de clase; md son los 900px que usa el resto.
    return (
        <Box id="grupo-n2" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: { xs: "1.25rem", md: "1.5rem" }
                        }}
                    >
                        <PlaylistAddCheckIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                        {order?.nombre || "Grupo de Nave 2"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Puntea una vez y las unidades se reparten entre los pedidos del grupo
                    </Typography>

                    {/* Información del grupo */}
                    {!loading && pedidosArray.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <InfoGrupo order={order} pedidos={pedidosArray} />
                        </>
                    )}
                </Box>

                {/* Contenido */}
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 8
                        }}
                    >
                        <CircularProgress size={40} sx={{ color: "#cc071e" }} />
                    </Box>
                ) : (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                        <ListOrder
                            nave={'2'}
                            type={'albaran'}
                            registerAction={registerSaleGrupo}
                            handle={'ventas'}
                            read={false}
                            preparer={true}
                            reviewer={false}
                            camera={false}
                            orderStateN2={true}
                            onAssignToOrder={handleAssignToOrder}
                            autoFocusEan={punteoEnMarcha}
                        />
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default OrderN2Grupo;
